/* eslint-disable no-restricted-globals */ // We intentionally use `history.state` here to preserve navigation state on refresh. Angular's Router extras.state doesn't persist after a page reload.

import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from 'src/app/core/services/auth.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { MaskEmailPipe } from 'src/app/shared/pipes/mask-email.pipe';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { interval, Subscription } from 'rxjs';

@Component({
  selector: 'app-otp-verification',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HttpClientModule,
    TranslateModule,
    PasswordModule,
    ButtonModule,
    FormsModule,
    MaskEmailPipe,
  ],
  providers: [AuthService, TranslateService],
  templateUrl: './otp-verification.component.html',
  styleUrl: './otp-verification.component.scss',
})
export class OtpVerificationComponent implements OnInit, OnDestroy {
  loginForm: FormGroup;

  isLoading = false;

  authError: string | null = null;

  otp: string[] = ['', '', '', ''];

  email = '';

  resendTimeLeft = 0;

  private timerSub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toaster: ToasterService,
    private translate: TranslateService,
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.pattern('^.*[a-zA-Z0-9].{3,}')]],
    });
  }

  ngOnInit() {
    this.translate.setDefaultLang('en');
    this.translate.use('en');
    const navState = history.state;
    this.email = navState?.email || null;
    history.replaceState({}, '', this.router.url);

    if (!this.email) {
      this.router.navigate(['/signIn']);
    }

    this.startResendTimer();
  }

  ngOnDestroy() {
    this.email = '';
    history.state.email = null;
    this.timerSub?.unsubscribe();
  }

  switchLanguage(language: string) {
    this.translate.use(language);
  }

  onOtpChange(index: number, value: string) {
    this.otp[index] = value;

    if (value && index < this.otp.length - 1) {
      const nextInput = document.querySelector(`input[name="otp${index + 1}"]`) as HTMLInputElement;
      nextInput?.focus();
    }
  }

  onSubmit() {
    const fullOtp = this.otp.join('');

    if (fullOtp.length !== 4) {
      return;
    }

    this.authService.verifyOtp(this.email, fullOtp).subscribe({
      next: (response) => {
        const data = response?.data;

        if (data?.token && data?.user) {
          localStorage.setItem('authToken', data.token);
          localStorage.setItem('userData', JSON.stringify(data.user));

          if (data.authTokenExpiresOn) {
            localStorage.setItem('authTokenExpiresOn', data.authTokenExpiresOn);
          }

          this.router.navigate(['/dashboard']);
        }
      },
      error: () => {},
    });
  }

  onOtpInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const { value } = input;

    if (value.length > 1) {
      input.value = value.charAt(value.length - 1);
      this.otp[index] = input.value;
    }

    if (value && index < this.otp.length - 1) {
      const nextInput = document.querySelector(`input[name='otp${index + 1}']`) as HTMLInputElement;
      nextInput?.focus();
    }
  }

  onOtpKeyDown(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace' || event.key === 'Delete') {
      if (!input.value && index > 0) {
        const prevInput = document.querySelector(
          `input[name='otp${index - 1}']`,
        ) as HTMLInputElement;
        prevInput?.focus();
      }
    }
  }

  resendOtp() {
    this.startResendTimer();
    try {
      this.authService.resendOtp(this.email).subscribe();
    } catch (error) {
      this.toaster.showErrorToast('Error resending OTP!');
    }
  }

  private startResendTimer() {
    this.resendTimeLeft = 60;
    this.timerSub?.unsubscribe();
    this.timerSub = interval(1000).subscribe(() => {
      if (this.resendTimeLeft > 0) {
        this.resendTimeLeft -= 1;
      } else {
        this.timerSub?.unsubscribe();
      }
    });
  }
}
